import axios from 'axios'

export interface PendingVerificationSession {
  accessToken: string
  refreshToken?: string
  email?: string
  intentUuid?: string
  expiresAt?: string
}

export interface FullCredentialExchange {
  access_token: string
  refresh_token?: string
  client_api_key: string
  user?: unknown
  intended?: string
}

export interface VerifiedDeploymentHandoff {
  intent_uuid: string
  server_uuid: string
  game_slug: string
  template_name: string
}

const STORAGE_KEY = 'peltast:pending-verification-session'
type JsonRecord = Record<string, unknown>

function record(value: unknown): JsonRecord {
  return value && typeof value === 'object' ? value as JsonRecord : {}
}

function responseBody(value: unknown): JsonRecord {
  const outer = record(value)
  const data = record(outer.data)
  return record(data.data ?? outer.data ?? value)
}

export function verificationErrorMessage(value: unknown, fallback = 'Verification request failed'): string {
  if (typeof value === 'string' && value.trim()) return value
  if (value instanceof Error && value.message) return value.message
  if (value && typeof value === 'object') {
    const body = value as JsonRecord
    for (const key of ['message', 'error', 'detail', 'title']) {
      const nested = verificationErrorMessage(body[key], '')
      if (nested) return nested
    }
  }
  return fallback
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

export function isPendingVerificationResponse(value: unknown): boolean {
  const body = responseBody(value)
  return Boolean(
    body?.scope === 'email_verification'
    || body?.pending_verification === true
    || body?.pending_session
    || body?.verification_session
  )
}

export function pendingSessionFromResponse(value: unknown): PendingVerificationSession | null {
  const body = responseBody(value)
  if (!isPendingVerificationResponse(body)) return null
  const pending = record(body.pending_session ?? body.verification_session ?? body)
  const accessToken = pending.access_token ?? pending.token ?? body.access_token ?? body.token
  if (!accessToken) return null
  return {
    accessToken: String(accessToken),
    refreshToken: pending.refresh_token || body.refresh_token ? String(pending.refresh_token ?? body.refresh_token) : undefined,
    email: pending.email || body.email ? String(pending.email ?? body.email) : undefined,
    intentUuid: pending.intent_uuid || body.intent_uuid ? String(pending.intent_uuid ?? body.intent_uuid) : undefined,
    expiresAt: pending.expires_at || body.expires_at ? String(pending.expires_at ?? body.expires_at) : undefined,
  }
}

export function credentialExchangeFromResponse(value: unknown): FullCredentialExchange | null {
  const body = responseBody(value)
  const credentials = record(body.credentials ?? body.full_credentials ?? body)
  if (!credentials.access_token || !credentials.client_api_key) return null
  return {
    access_token: String(credentials.access_token),
    refresh_token: credentials.refresh_token ? String(credentials.refresh_token) : undefined,
    client_api_key: String(credentials.client_api_key),
    user: credentials.user ?? body.user,
    intended: credentials.intended || body.intended ? String(credentials.intended ?? body.intended) : undefined,
  }
}

export function verifiedDeploymentFromResponse(value: unknown): VerifiedDeploymentHandoff | null {
  const deployment = record(responseBody(value).deployment)
  if (!deployment.server_uuid || !deployment.game_slug) return null
  return {
    intent_uuid: String(deployment.intent_uuid || ''),
    server_uuid: String(deployment.server_uuid),
    game_slug: String(deployment.game_slug),
    template_name: String(deployment.template_name || deployment.game_slug),
  }
}

class PendingVerificationSessionClient {
  private refreshPromise: Promise<boolean> | null = null

  load(): PendingVerificationSession | null {
    const raw = storage()?.getItem(STORAGE_KEY)
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw) as PendingVerificationSession
      if (!parsed.accessToken) throw new Error('Missing access token')
      if (parsed.expiresAt && Date.parse(parsed.expiresAt) <= Date.now()) {
        this.clear()
        return null
      }
      return parsed
    } catch {
      this.clear()
      return null
    }
  }

  capture(value: unknown): PendingVerificationSession | null {
    const session = pendingSessionFromResponse(value)
    if (!session) return null
    try {
      window.localStorage.removeItem('auth_token')
      window.localStorage.removeItem('refresh_token')
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
    storage()?.setItem(STORAGE_KEY, JSON.stringify(session))
    return session
  }

  clear(): void {
    storage()?.removeItem(STORAGE_KEY)
  }

  async request<T = unknown>(method: 'get' | 'post' | 'patch', url: string, data?: unknown, headers?: Record<string, string>): Promise<T> {
    let session = this.load()
    if (!session) throw new Error('Verification session is unavailable or expired')
    let response = await axios.request<T>({
      method,
      url,
      data,
      headers: { Authorization: `Bearer ${session.accessToken}`, ...headers },
      validateStatus: status => status < 500,
    })
    if (response.status === 401 && session.refreshToken && await this.refresh()) {
      session = this.load()
      if (!session) throw new Error('Verification session expired')
      response = await axios.request<T>({
        method,
        url,
        data,
        headers: { Authorization: `Bearer ${session.accessToken}`, ...headers },
        validateStatus: status => status < 500,
      })
    }
    if (response.status >= 400) {
      const errorBody = record(response.data)
      throw Object.assign(new Error(verificationErrorMessage(errorBody)), { status: response.status })
    }
    return response.data
  }

  async refresh(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise
    this.refreshPromise = (async () => {
      const current = this.load()
      if (!current?.refreshToken) return false
      try {
        const response = await axios.post('/hoard-auth/token/refresh', {
          refresh_token: current.refreshToken,
        }, { validateStatus: status => status < 500 })
        if (response.status !== 200 || !isPendingVerificationResponse(response.data)) return false
        const refreshed = pendingSessionFromResponse(response.data)
        if (!refreshed) return false
        this.capture({
          pending_session: {
            ...current,
            access_token: refreshed.accessToken,
            refresh_token: refreshed.refreshToken ?? current.refreshToken,
            email: refreshed.email ?? current.email,
            intent_uuid: refreshed.intentUuid ?? current.intentUuid,
            expires_at: refreshed.expiresAt ?? current.expiresAt,
          },
        })
        return true
      } catch {
        return false
      } finally {
        this.refreshPromise = null
      }
    })()
    return this.refreshPromise
  }

  status<T = unknown>(): Promise<T> {
    return this.request('get', '/hoard-auth/email-verification/status')
  }

  resend<T = unknown>(): Promise<T> {
    return this.request('post', '/hoard-auth/email-verification/send', {})
  }

  verify<T = unknown>(code: string): Promise<T> {
    return this.request('post', '/hoard-auth/email-verification/verify', { code })
  }

  deploymentStatus<T = unknown>(intentUuid?: string): Promise<T> {
    const path = intentUuid
      ? `/hoard-api/registration-deployments/${encodeURIComponent(intentUuid)}/status`
      : '/hoard-api/registration-deployments/status'
    return this.request('get', path)
  }

  changeEmail<T = unknown>(email: string): Promise<T> {
    return this.request('patch', '/hoard-auth/email-verification/email', { email })
  }

  cancelDeployment<T = unknown>(intentUuid: string): Promise<T> {
    return this.request('post', `/hoard-api/registration-deployments/${encodeURIComponent(intentUuid)}/cancel`, {})
  }

  async signOut(): Promise<void> {
    const current = this.load()
    if (current?.refreshToken) {
      try {
        await axios.post('/hoard-auth/token/revoke', { refresh_token: current.refreshToken }, {
          validateStatus: status => status < 500,
        })
      } catch { /* local sign-out still completes */ }
    }
    this.clear()
  }
}

export const pendingVerificationSession = new PendingVerificationSessionClient()

export class PendingVerificationRequired extends Error {
  constructor() {
    super('Email verification is required')
    this.name = 'PendingVerificationRequired'
  }
}
